import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { validateEmailAttachments, EMAIL_ATTACHMENT_MAX_MB } from "@/lib/file-utils";
import { plainTextToHtml } from "@/lib/email-utils";
import { apiRequest, queryClient } from "@/lib/query-client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { EmailPillInput } from "@/components/ui/email-pill-input";
import {
  ContactQuickPicks,
  normalizeEmail,
  type ClaimQuickPickContact,
} from "@/components/email/contact-quick-picks";
import {
  Mail,
  Send,
  Save,
  Sparkles,
  Loader2,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  Pen,
  Paperclip,
  Upload,
  X,
  Check,
} from "lucide-react";

// Strips HTML tags from a string, preserving readable text content with line breaks
export function stripHtmlToPlainText(html: string): string {
  if (!html) return "";
  let text = html;
  text = text.replace(/<br\s*\/?>/gi, "\n");
  text = text.replace(/<\/p>/gi, "\n");
  text = text.replace(/<\/div>/gi, "\n");
  const temp = document.createElement("div");
  temp.innerHTML = text;
  const result = temp.textContent || temp.innerText || "";
  return result.replace(/\n{3,}/g, "\n\n").trim();
}

const EMAIL_TEMPLATES = [
  {
    id: "initial_contact",
    name: "Initial Claim Contact",
    subject: "Re: Your Insurance Claim",
    body: `<p>Dear [Client Name],</p><p>Thank you for contacting Mission Property Loss Consultants. We have received your claim information and are ready to assist you in maximizing your insurance settlement.</p><p>We will review your policy and damage assessment, and will be in touch within 24 hours to discuss next steps.</p><p>Best regards,<br>[Your Name]<br>Mission Property Loss Consultants</p>`,
  },
  {
    id: "status_update",
    name: "Claim Status Update",
    subject: "Claim Status Update - [Claim Number]",
    body: `<p>Dear [Client Name],</p><p>I wanted to provide you with an update on your claim progress:</p><p>[Update details here]</p><p>Please let me know if you have any questions or concerns.</p><p>Best regards,<br>[Your Name]<br>Mission Property Loss Consultants</p>`,
  },
  {
    id: "document_request",
    name: "Document Request",
    subject: "Additional Documentation Needed",
    body: `<p>Dear [Client Name],</p><p>To proceed with your claim, we need the following documentation:</p><ol><li>[Document 1]</li><li>[Document 2]</li></ol><p>Please send these at your earliest convenience.</p><p>Best regards,<br>[Your Name]<br>Mission Property Loss Consultants</p>`,
  },
  {
    id: "follow_up",
    name: "Claim Follow-up",
    subject: "Following Up - [Claim Number]",
    body: `<p>Dear [Client Name],</p><p>I hope this email finds you well. I wanted to follow up regarding [topic].</p><p>[Follow-up details]</p><p>Please feel free to reach out if you have any questions.</p><p>Best regards,<br>[Your Name]<br>Mission Property Loss Consultants</p>`,
  },
];

interface EmailMessage {
  gmailMessageId?: string;
  subject?: string;
  from?: string;
  to?: string;
  body?: string;
}

interface EmailDraft {
  id?: string;
  to: string;
  cc?: string;
  bcc?: string;
  subject: string;
  body: string;
}

export interface EmailComposerProps {
  isOpen: boolean;
  onClose: () => void;
  // Claim context (optional)
  claimId?: string;
  claimNumber?: string;
  insuranceCarrier?: string;
  claimContacts?: ClaimQuickPickContact[];
  claimDocuments?: any[];
  onOpenAIDraft?: () => void;
  aiDraftSubject?: string;
  aiDraftBody?: string;
  onBodyChange?: (body: string) => void;
  // Gmail context (optional)
  replyTo?: EmailMessage;
  forwardEmail?: EmailMessage;
  draft?: EmailDraft | null;
  onDraftSaved?: () => void;
  onSent?: () => void;
  // Defaults
  defaultTo?: string;
  defaultSubject?: string;
  // When true: renders inline (no portal), fills the window, hides drag handle and overlay
  isPopupMode?: boolean;
}

function splitEmails(raw: string | undefined): string[] {
  if (!raw) return [];
  return raw.split(",").map(e => e.trim()).filter(Boolean);
}

function toggleEmail(list: string[], email: string): string[] {
  const key = normalizeEmail(email);
  if (list.some((item) => normalizeEmail(item) === key)) {
    return list.filter((item) => normalizeEmail(item) !== key);
  }
  return [...list, email];
}

export function EmailComposer({
  isOpen,
  onClose,
  claimId,
  claimNumber,
  claimContacts = [],
  claimDocuments,
  onOpenAIDraft,
  aiDraftSubject,
  aiDraftBody,
  onBodyChange,
  replyTo,
  forwardEmail,
  draft,
  onDraftSaved,
  onSent,
  defaultTo,
  defaultSubject,
  isPopupMode = false,
}: EmailComposerProps) {
  const { toast } = useToast();

  const [to, setTo] = useState<string[]>([]);
  const [cc, setCc] = useState<string[]>([]);
  const [bcc, setBcc] = useState<string[]>([]);
  const [showCcBcc, setShowCcBcc] = useState(true);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [attachedDocIds, setAttachedDocIds] = useState<string[]>([]);
  const [localFiles, setLocalFiles] = useState<File[]>([]);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [saveStatus, setSaveStatus] = useState<"idle" | "unsaved" | "saving" | "saved">("idle");
  const [addressQuery, setAddressQuery] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Guards against a double-submit: two synchronous clicks fire before React
  // re-renders the (now disabled) Send button, so the disabled attribute alone
  // can't stop the second click — this ref does, synchronously.
  const sendInFlightRef = useRef(false);
  const debounceTimerRef = useRef<NodeJS.Timeout>();
  const queryDebounceRef = useRef<NodeJS.Timeout>();
  // Poll-loop lifecycle. mountedRef flips false on unmount so the async send/poll
  // loop stops (no more fetches, no toasts, no state setters). pollTimerRef holds
  // the pending 2 s interval timer so unmount can clear it (no leftover timers).
  // pollAbortRef aborts the in-flight poll GET on timeout or unmount.
  const mountedRef = useRef(true);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const pollAbortRef = useRef<AbortController | null>(null);

  // Drag-to-reposition state
  const panelRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const [dragged, setDragged] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });

  // Fetch Gmail accounts
  const { data: gmailAccounts = [] } = useQuery({
    queryKey: ["/api/gmail/accounts"],
    enabled: isOpen,
  });
  const hasGmailAccount = (gmailAccounts as any[]).length > 0;
  const activeAccount = (gmailAccounts as any[]).find((a: any) => a.isActive) || (gmailAccounts as any[])[0];

  // Fetch signature
  const { data: signatureData } = useQuery({
    queryKey: ["/api/gmail/signature"],
    enabled: hasGmailAccount,
    retry: 1,
  });

  // Fetch user profile (for app-level signature)
  const { data: userProfile } = useQuery({
    queryKey: ["/api/auth/user"],
    enabled: isOpen,
    retry: 1,
  });

  // Fetch address suggestions
  const { data: addressSuggestions = [] } = useQuery({
    queryKey: ["/api/gmail/address-suggestions", { q: addressQuery }],
    enabled: addressQuery.length >= 2,
    staleTime: 30_000,
  });

  const { data: globalContactsData } = useQuery({
    queryKey: ["/api/contacts", { search: addressQuery }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (addressQuery) params.set("search", addressQuery);
      const res = await apiRequest("GET", `/api/contacts?${params}`);
      return res.json() as Promise<{ contacts: Array<{
        id: string;
        firstName: string | null;
        lastName: string | null;
        email: string | null;
        contactType: string;
        lastSeenInEmail: string | null;
        emailOccurrenceCount: number | null;
      }> }>;
    },
    enabled: addressQuery.length >= 2,
    staleTime: 30_000,
  });
  const globalContacts = globalContactsData?.contacts ?? [];

  const handleQueryChange = useCallback((q: string) => {
    clearTimeout(queryDebounceRef.current);
    queryDebounceRef.current = setTimeout(() => setAddressQuery(q), 300);
  }, []);

  const hasSignature = !!(signatureData as any)?.signature || !!(userProfile as any)?.emailSignature;

  // Initialize fields on open
  useEffect(() => {
    if (!isOpen) return;

    if (replyTo) {
      setTo(replyTo.from ? [replyTo.from] : []);
      setSubject(replyTo.subject?.startsWith("Re:") ? replyTo.subject : `Re: ${replyTo.subject || ""}`);
      setBody(`<p></p><hr /><p><strong>------- Original Message -------</strong><br />From: ${replyTo.from}<br />Subject: ${replyTo.subject}</p><p>${replyTo.body || ""}</p>`);
    } else if (forwardEmail) {
      setTo([]);
      setSubject(forwardEmail.subject?.startsWith("Fwd:") ? forwardEmail.subject : `Fwd: ${forwardEmail.subject || ""}`);
      setBody(`<p></p><hr /><p><strong>------- Forwarded Message -------</strong><br />From: ${forwardEmail.from}<br />To: ${forwardEmail.to}<br />Subject: ${forwardEmail.subject}</p><p>${forwardEmail.body || ""}</p>`);
    } else if (draft) {
      setTo(splitEmails(draft.to));
      setCc(splitEmails(draft.cc));
      setBcc(splitEmails(draft.bcc));
      if (draft.cc || draft.bcc) setShowCcBcc(true);
      setSubject(draft.subject || "");
      setBody(plainTextToHtml(draft.body || ''));
      if (draft.id) setDraftId(draft.id);
    } else {
      setTo(splitEmails(defaultTo));
      if (claimNumber && !subject) {
        setSubject(claimNumber.replace(/[-\s]/g, ""));
      } else if (defaultSubject) {
        setSubject(defaultSubject);
      }
      const sig = (signatureData as any)?.signature;
      if (sig && !body) {
        setBody(`<p></p><p>${stripHtmlToPlainText(sig)}</p>`);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, replyTo, forwardEmail, draft, defaultTo, defaultSubject]);

  // Apply AI draft content — reactive so it fires whenever props change (not just on mount)
  useEffect(() => {
    if (aiDraftSubject) setSubject(aiDraftSubject);
  }, [aiDraftSubject]);

  useEffect(() => {
    if (aiDraftBody) {
      // Guard: if content is already HTML, use it as-is; otherwise convert plain text
      const isHtml = aiDraftBody.trimStart().startsWith('<');
      const htmlBody = isHtml ? aiDraftBody : plainTextToHtml(aiDraftBody);
      const sig = (signatureData as any)?.signature;
      setBody(sig ? `${htmlBody}<p></p><p>${stripHtmlToPlainText(sig)}</p>` : htmlBody);
    }
  }, [aiDraftBody]);

  // Notify parent of body changes
  useEffect(() => {
    onBodyChange?.(body);
  }, [body, onBodyChange]);

  const saveDraftToServer = useCallback(async () => {
    if (!to.length && !subject && !body) return;
    const fromAccountId = activeAccount?.id;
    const payload = {
      to: to.join(", "),
      cc: cc.join(", ") || undefined,
      bcc: bcc.join(", ") || undefined,
      subject,
      body,
      claimId: claimId || undefined,
      attachmentMetadata: fromAccountId ? { fromAccountId } : undefined,
    };
    try {
      if (draftId) {
        await apiRequest("PUT", `/api/gmail/drafts/${draftId}`, payload);
      } else {
        const res = await apiRequest("POST", "/api/gmail/drafts", payload);
        const saved = await res.json();
        setDraftId(saved.id);
      }
      // onDraftSaved is NOT called here — auto-save must be silent.
      // It is called only from the explicit Save Draft button handler (Step 2).
    } catch {
      // silent — draft save is best-effort
    }
  }, [to, cc, bcc, subject, body, claimId, activeAccount, draftId]);

  // Debounced auto-save
  useEffect(() => {
    if (!isOpen || (!to.length && !subject && !body)) return;
    setSaveStatus("unsaved");
    clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(async () => {
      setSaveStatus("saving");
      await saveDraftToServer();
      setSaveStatus("saved");
    }, 1500);
    return () => clearTimeout(debounceTimerRef.current);
  }, [to, cc, bcc, subject, body, isOpen]);

  // Save on beforeunload
  useEffect(() => {
    if (!isOpen) return;
    const handler = () => { saveDraftToServer(); };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isOpen, saveDraftToServer]);

  // Drag tracking
  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging.current) return;
      setPos({ x: e.clientX - dragOffset.current.x, y: e.clientY - dragOffset.current.y });
    };
    const onMouseUp = () => { isDragging.current = false; };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  // On unmount: stop the poll loop (mountedRef), clear its pending interval timer
  // so no timers survive, and abort any in-flight poll GET.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimeout(pollTimerRef.current);
      pollAbortRef.current?.abort();
    };
  }, []);

  const toggleDoc = (docId: string) => {
    setAttachedDocIds(prev =>
      prev.includes(docId) ? prev.filter(id => id !== docId) : [...prev, docId]
    );
  };

  const handleLocalFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selected = Array.from(e.target.files);
      const { valid, oversized } = validateEmailAttachments(selected);
      if (oversized.length) {
        toast({
          title: "Attachment too large",
          description: `Max ${EMAIL_ATTACHMENT_MAX_MB} MB. Skipped: ${oversized.join(", ")}`,
          variant: "destructive",
        });
      }
      setLocalFiles(prev => [...prev, ...valid]);
    }
    e.target.value = "";
  };

  const handleSendEmail = async () => {
    // Synchronous re-entrancy guard: a second click before the re-render is dropped.
    if (sendInFlightRef.current) return;
    sendInFlightRef.current = true;
    setIsSendingEmail(true);
    // One idempotency key per send attempt: the server collapses duplicate POSTs
    // carrying the same key (e.g. a network-level retry) into a single queued job.
    const clientRequestId =
      (globalThis.crypto as any)?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    try {
      if (!to.length) {
        toast({ title: "Validation Error", description: "Please enter a recipient email address.", variant: "destructive" });
        return;
      }
      if (!subject.trim()) {
        toast({ title: "Validation Error", description: "Please enter an email subject.", variant: "destructive" });
        return;
      }
      if (!body.trim()) {
        toast({ title: "Validation Error", description: "Please enter an email body.", variant: "destructive" });
        return;
      }

      const accountsResponse = await fetch("/api/gmail/accounts", { credentials: "include" });
      if (!accountsResponse.ok) {
        if (accountsResponse.status === 401) throw new Error("Session expired. Please refresh the page and try again.");
        throw new Error("Failed to fetch email accounts. Please try again.");
      }
      const emailAccounts = await accountsResponse.json();
      if (!emailAccounts || emailAccounts.length === 0) {
        toast({ title: "No Email Account", description: "Please connect an email account in Settings to send emails.", variant: "destructive" });
        return;
      }

      const primaryAccount = emailAccounts.find((a: any) => a.isActive) || emailAccounts[0];
      if (!primaryAccount.isActive) {
        toast({ title: "Email Disconnected", description: "Your Gmail account is disconnected. Please reconnect in Settings.", variant: "destructive" });
        return;
      }
      const toStr = to.join(", ");
      const ccStr = cc.join(", ") || undefined;
      const bccStr = bcc.join(", ") || undefined;
      const textBody = stripHtmlToPlainText(body);

      const hasAttachments = attachedDocIds.length > 0 || localFiles.length > 0;
      let response: Response;

      if (hasAttachments) {
        const formData = new FormData();
        formData.append("to", toStr);
        formData.append("subject", subject.trim());
        formData.append("text", textBody);
        formData.append("html", body);
        formData.append("fromAccountId", primaryAccount.id);
        formData.append("clientRequestId", clientRequestId);
        if (claimId) formData.append("claimId", claimId);
        if (ccStr) formData.append("cc", ccStr);
        if (bccStr) formData.append("bcc", bccStr);
        for (const file of localFiles) {
          formData.append("attachments", file);
        }
        for (const docId of attachedDocIds) {
          const doc = claimDocuments?.find((d: any) => d.id === docId);
          const docName = doc?.displayName || doc?.fileName || `document-${docId}`;
          const resp = await fetch(`/api/documents/${docId}/download`, { credentials: "include" });
          // A failed download returns an error body, not the file — attaching it would
          // silently send a broken "document". Abort the whole send and name the doc.
          if (!resp.ok) {
            throw new Error(`Could not attach "${docName}". The email was not sent.`);
          }
          const blob = await resp.blob();
          formData.append("attachments", blob, docName);
        }
        // Route the multipart send through apiRequest so it carries the
        // x-csrf-token header (read from the csrf-token cookie); a raw fetch here
        // omits it and the server rejects the send with "CSRF token validation failed".
        response = await apiRequest("POST", "/api/gmail/send", formData);
      } else {
        const payload: any = {
          to: toStr,
          subject: subject.trim(),
          text: textBody,
          html: body,
          fromAccountId: primaryAccount.id,
          claimId,
          clientRequestId,
        };
        if (ccStr) payload.cc = ccStr;
        if (bccStr) payload.bcc = bccStr;
        response = await apiRequest("POST", "/api/gmail/send", payload);
      }

      if (response.status === 202) {
        // Async path: poll background job until email is sent
        const { jobId } = await response.json().catch(() => ({}));
        toast({ title: "Sending email…", description: "This may take a few seconds." });

        // Poll until the send reaches a terminal state. Budget covers the server
        // worst case: 3 attempts x 5 min startToClose + 15 s retry backoff, plus
        // the workflow's mark-failed activity (1 min x 3 attempts) that writes the
        // terminal 'failed' status. The old 30-poll (60 s) budget gave up while
        // the send was still being retried, so the user re-clicked and sent twice.
        const POLL_INTERVAL_MS = 2000;
        const POLL_BUDGET_MS = 20 * 60 * 1000;
        // A single poll GET must never block the loop. Race it against this
        // timeout so a hung request is treated as a poll error and polling continues.
        const POLL_REQUEST_TIMEOUT_MS = 30_000;
        const deadline = Date.now() + POLL_BUDGET_MS;
        let consecutivePollErrors = 0;
        while (Date.now() < deadline) {
          // Composer unmounted (closed / navigated away): stop entirely — no more
          // fetches, no toasts, no state setters.
          if (!mountedRef.current) return;
          await new Promise((r) => { pollTimerRef.current = setTimeout(r, POLL_INTERVAL_MS); });
          if (!mountedRef.current) return;
          let job: any;
          // Each poll GET gets its own AbortController so the request timeout (or an
          // unmount) actually aborts the fetch instead of leaving it open.
          const controller = new AbortController();
          pollAbortRef.current = controller;
          try {
            let timer: ReturnType<typeof setTimeout>;
            const timeout = new Promise<never>((_, reject) => {
              timer = setTimeout(() => {
                controller.abort();
                reject(new Error("poll request timed out"));
              }, POLL_REQUEST_TIMEOUT_MS);
            });
            try {
              job = await Promise.race([
                apiRequest("GET", `/api/background-jobs/${jobId}`, undefined, { signal: controller.signal }).then((jobRes) => jobRes.json()),
                timeout,
              ]);
            } finally {
              clearTimeout(timer!);
            }
            consecutivePollErrors = 0;
          } catch {
            // A failed/thrown/timed-out/aborted poll is NOT a send failure — the
            // send may still be in flight. Keep polling; only give up (to the
            // "still sending" terminal branch) after many consecutive poll
            // failures. But if we were unmounted, stop silently.
            if (!mountedRef.current) return;
            if (++consecutivePollErrors >= 10) break;
            continue;
          }
          if (!mountedRef.current) return;
          if (job.status === "completed") {
            toast({ title: "Email Sent", description: `Email sent successfully to ${toStr}` });
            if (draftId) {
              apiRequest("DELETE", `/api/gmail/drafts/${draftId}`).catch(() => {});
              setDraftId(null);
            }
            if (claimId) {
              queryClient.invalidateQueries({ queryKey: [`/api/claims/${claimId}/communications`] });
              queryClient.invalidateQueries({ queryKey: [`/api/claims/${claimId}/timeline`] });
            }
            onSent?.();
            handleClose();
            return;
          }
          if (job.status === "failed") {
            throw new Error(job.error || "Failed to send email. Please try again.");
          }
          // Any other status ('running', 'pending', …): keep polling. Send stays
          // disabled showing "Sending…".
        }
        if (!mountedRef.current) return;
        toast({ title: "Email still sending", description: "Check back shortly.", variant: "default" });
        onSent?.();
        handleClose();
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: "Failed to send email" }));
        if (response.status === 401) throw new Error("Session expired. Please refresh the page and try again.");
        if (response.status === 404) {
          // EMAIL_ACCOUNT_NOT_FOUND (or foreign mailbox): show the server's message.
          throw new Error(errorData.message || "Email account not found. Please connect an account in Settings.");
        }
        if (response.status === 403) {
          if (errorData.requiresReconnect || errorData.code === "EMAIL_RECONNECT_REQUIRED") {
            throw new Error(errorData.message || "Your email account is disconnected. Please reconnect in Settings.");
          }
          // SHARED_MAILBOX_UNAVAILABLE and other send refusals: surface the server's message.
          throw new Error(errorData.message || "You don't have permission to send emails.");
        }
        if (response.status === 429) throw new Error("Too many requests. Please wait a moment and try again.");
        if (response.status === 503 && errorData.code === "QUEUE_UNAVAILABLE") throw new Error(errorData.message);
        if (response.status >= 500) throw new Error("Server error. Please try again in a few moments.");
        throw new Error(errorData.message || "Failed to send email. Please try again.");
      }

      // Legacy synchronous path (fallback)
      const responseData = await response.json().catch(() => ({}));

      if (!claimId && responseData.claimLinked === false) {
        toast({ title: "Email Sent", description: "Email sent but not linked to a claim" });
      } else {
        toast({ title: "Email Sent", description: `Email sent successfully to ${toStr}` });
      }

      if (draftId) {
        apiRequest("DELETE", `/api/gmail/drafts/${draftId}`).catch(() => {});
        setDraftId(null);
      }

      if (claimId) {
        queryClient.invalidateQueries({ queryKey: [`/api/claims/${claimId}/communications`] });
        queryClient.invalidateQueries({ queryKey: [`/api/claims/${claimId}/timeline`] });
      }

      onSent?.();
      handleClose();
    } catch (error) {
      // Don't toast after unmount.
      if (mountedRef.current) {
        toast({
          title: "Send Failed",
          description: error instanceof Error ? error.message : "Failed to send email. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      // After unmount, skip the state setter (nothing to update); the ref reset is harmless.
      if (mountedRef.current) {
        setIsSendingEmail(false);
      }
      sendInFlightRef.current = false;
    }
  };

  const handleClose = useCallback(async () => {
    await saveDraftToServer();
    setTo([]);
    setCc([]);
    setBcc([]);
    setShowCcBcc(true);
    setSubject("");
    setBody("");
    setLocalFiles([]);
    setAttachedDocIds([]);
    setSelectedTemplate("");
    setDraftId(null);
    setSaveStatus("idle");
    onClose();
  }, [saveDraftToServer, onClose]);

  // Escape to close — placed after handleClose to avoid TDZ error
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") handleClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen, handleClose]);

  const applyTemplate = (templateId: string) => {
    const t = EMAIL_TEMPLATES.find(t => t.id === templateId);
    if (t) {
      setSubject(t.subject);
      setBody(t.body);
      toast({ title: "Template applied", description: `"${t.name}" template applied` });
    }
  };

  const sig = (userProfile as any)?.emailSignature || (signatureData as any)?.signature || "";

  const onHeaderMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest("button,input,select,textarea,a")) return;
    const rect = panelRef.current!.getBoundingClientRect();
    isDragging.current = true;
    setDragged(true);
    setPos({ x: rect.left, y: rect.top });
    dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  if (!isOpen) return null;

  const showContactQuickPicks = Boolean(
    claimId && claimContacts.some((c) => c.email?.trim()),
  );

  const ccQuickPicks = showContactQuickPicks ? (
    <ContactQuickPicks
      field="cc"
      contacts={claimContacts}
      selectedEmails={cc}
      occupiedEmails={to}
      onToggle={(email) => {
        setShowCcBcc(true);
        setCc((current) => toggleEmail(current, email));
      }}
    />
  ) : null;

  const panelContent = (
    <>
      {/* Dim overlay — only in normal (non-popup) mode */}
      {!isPopupMode && (
        <div className="fixed inset-0 z-[51] bg-black/20 pointer-events-none animate-in fade-in duration-200" aria-hidden="true" />
      )}

      {/* Panel — fill-window in popup mode, draggable floating panel otherwise */}
      <div
        ref={isPopupMode ? undefined : panelRef}
        role="dialog"
        aria-modal={isPopupMode ? "true" : "false"}
        aria-labelledby="email-composer-title"
        style={isPopupMode
          ? { position: "fixed", inset: 0, width: "100%", height: "100%", zIndex: 1 }
          : dragged
            ? { left: pos.x, top: pos.y, transform: "none" }
            : { left: "50%", top: "5%", transform: "translateX(-50%)" }
        }
        className={isPopupMode
          ? "overflow-hidden flex flex-col bg-background"
          : `fixed z-[52] w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col bg-background border border-border rounded-lg shadow-xl${!dragged ? " animate-in fade-in zoom-in-95 slide-in-from-top-4 duration-200" : ""}`
        }
      >
        {/* Header — no drag cursor/handler in popup mode */}
        <div
          className={`flex items-start justify-between px-6 pt-5 pb-4 shrink-0 border-b border-border${!isPopupMode ? " cursor-grab active:cursor-grabbing select-none" : ""}`}
          onMouseDown={isPopupMode ? undefined : onHeaderMouseDown}
        >
          <div className="space-y-0.5">
            <h2 id="email-composer-title" className="text-lg font-semibold leading-none tracking-tight flex items-center">
              <Mail className="h-5 w-5 mr-2 shrink-0" />
              {replyTo ? "Reply to Email" : forwardEmail ? "Forward Email" : claimNumber ? `Send Email - Claim ${claimNumber}` : "Compose Email"}
            </h2>
            {activeAccount && (
              <p className="text-xs text-muted-foreground pl-7">Sending from: {activeAccount.email}</p>
            )}
          </div>
          <button
            className="ml-4 shrink-0 flex items-center justify-center w-8 h-8 rounded-md bg-destructive text-destructive-foreground hover:bg-destructive/80 transition-colors focus:outline-none focus:ring-2 focus:ring-ring"
            onClick={handleClose}
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto space-y-4 px-6 py-4">
          {/* Email templates (Gmail context only) */}
          {!claimId && (
            <div>
              <Label>Template (Optional)</Label>
              <Select value={selectedTemplate} onValueChange={(v) => { setSelectedTemplate(v); applyTemplate(v); }}>
                <SelectTrigger data-testid="select-email-template">
                  <SelectValue placeholder="Choose a template..." />
                </SelectTrigger>
                <SelectContent>
                  {EMAIL_TEMPLATES.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* To */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>To *</Label>
              {!showCcBcc && (
                <Button variant="ghost" size="sm" onClick={() => setShowCcBcc(true)} data-testid="button-show-cc-bcc" className="h-6 text-xs">
                  <ChevronDown className="h-3 w-3 mr-1" />Cc/Bcc
                </Button>
              )}
            </div>
            <EmailPillInput
              value={to}
              onChange={setTo}
              placeholder="recipient@example.com"
              suggestions={addressSuggestions as any[]}
              onQueryChange={handleQueryChange}
              claimContacts={claimContacts}
              globalContacts={globalContacts}
              data-testid="input-email-to"
            />
            {showContactQuickPicks && (
              <ContactQuickPicks
                field="to"
                contacts={claimContacts}
                selectedEmails={to}
                occupiedEmails={cc}
                onToggle={(email) => setTo((current) => toggleEmail(current, email))}
              />
            )}
          </div>

          {/* CC / BCC */}
          {showCcBcc && (
            <div className="space-y-4 animate-in slide-in-from-top-2 duration-200">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>Cc</Label>
                  <Button variant="ghost" size="sm" onClick={() => setShowCcBcc(false)} className="h-6">
                    <ChevronUp className="h-3 w-3" />
                  </Button>
                </div>
                <EmailPillInput
                  value={cc}
                  onChange={setCc}
                  placeholder="cc@example.com"
                  suggestions={addressSuggestions as any[]}
                  onQueryChange={handleQueryChange}
                  claimContacts={claimContacts}
                  globalContacts={globalContacts}
                  data-testid="input-email-cc"
                />
                {ccQuickPicks}
              </div>
              <div>
                <Label className="mb-1 block">Bcc</Label>
                <EmailPillInput
                  value={bcc}
                  onChange={setBcc}
                  placeholder="bcc@example.com"
                  suggestions={addressSuggestions as any[]}
                  onQueryChange={handleQueryChange}
                  claimContacts={claimContacts}
                  globalContacts={globalContacts}
                  data-testid="input-email-bcc"
                />
              </div>
            </div>
          )}

          {/* Subject */}
          <div>
            <Label>Subject *</Label>
            <Input
              placeholder="Email subject..."
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              data-testid="input-email-subject"
            />
          </div>

          {/* Body */}
          <div>
            <Label className="mb-1 block">Email Body *</Label>
            <div className="min-h-[200px] border rounded-md overflow-hidden">
              <RichTextEditor
                value={body}
                onChange={setBody}
                placeholder="Compose your email..."
              />
            </div>

            {hasSignature ? (
              <div className="mt-2 rounded-md border border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-950/20 p-2.5">
                <div className="flex items-center justify-between text-xs text-green-600 dark:text-green-400 mb-1.5">
                  <div className="flex items-center gap-1.5">
                    <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
                    <span className="font-medium">Signature will be auto-inserted</span>
                  </div>
                  <a href="/settings" className="text-muted-foreground hover:text-foreground underline">Edit</a>
                </div>
                <div className="text-xs text-muted-foreground whitespace-pre-wrap pl-[18px] max-h-16 overflow-y-auto">
                  {stripHtmlToPlainText(sig)}
                </div>
              </div>
            ) : hasGmailAccount ? (
              <div className="flex items-center gap-1.5 mt-1.5 text-xs text-muted-foreground">
                <Pen className="h-3 w-3 flex-shrink-0" />
                <span>No signature configured — <a href="/settings" className="underline hover:text-foreground">set one in Settings</a></span>
              </div>
            ) : null}
          </div>

          {/* Attachments */}
          <div>
            <Label>Attachments</Label>
            <div className="flex gap-2 mt-1">
              {claimDocuments && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="button-attach-documents">
                      <Paperclip className="h-4 w-4 mr-1" />Claim Docs
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-0" align="start">
                    <ScrollArea className="h-[250px]">
                      <div className="p-2 space-y-1">
                        {(claimDocuments || []).map((doc: any) => (
                          <button
                            key={doc.id}
                            onClick={() => toggleDoc(doc.id)}
                            className="w-full text-left px-3 py-2 rounded-md hover:bg-accent transition-colors flex items-center justify-between"
                          >
                            <span className="text-sm truncate">{doc.displayName || doc.fileName}</span>
                            {attachedDocIds.includes(doc.id) && <Check className="h-4 w-4 text-green-600 flex-shrink-0" />}
                          </button>
                        ))}
                      </div>
                    </ScrollArea>
                  </PopoverContent>
                </Popover>
              )}
              <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                <Upload className="h-4 w-4 mr-1" />Upload File
              </Button>
              <input ref={fileInputRef} type="file" multiple hidden onChange={handleLocalFiles} />
            </div>
            {(attachedDocIds.length > 0 || localFiles.length > 0) && (
              <div className="flex flex-wrap gap-1 mt-2">
                {attachedDocIds.map((id) => {
                  const doc = (claimDocuments || []).find((d: any) => d.id === id);
                  return (
                    <Badge key={id} variant="secondary" className="flex items-center gap-1" data-testid={`badge-attached-doc-${id}`}>
                      <span className="text-xs truncate max-w-[150px]">{doc?.displayName || doc?.fileName || id}</span>
                      <X
                        className="h-3 w-3 cursor-pointer"
                        onClick={() => setAttachedDocIds(prev => prev.filter(d => d !== id))}
                        data-testid={`remove-attached-doc-${id}`}
                      />
                    </Badge>
                  );
                })}
                {localFiles.map((f, i) => (
                  <Badge key={i} variant="secondary" className="flex items-center gap-1">
                    <span className="text-xs truncate max-w-[150px]">{f.name}</span>
                    <X className="h-3 w-3 cursor-pointer" onClick={() => setLocalFiles(prev => prev.filter((_, idx) => idx !== i))} />
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-border shrink-0">
          <span className="text-xs text-muted-foreground">
            {saveStatus === "saving" && "Auto-saving…"}
            {saveStatus === "saved" && "Draft saved"}
            {saveStatus === "unsaved" && "Unsaved changes"}
          </span>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={async () => { setSaveStatus("saving"); await saveDraftToServer(); setSaveStatus("saved"); onDraftSaved?.(); }}
              data-testid="button-save-draft"
            >
              <Save className="h-4 w-4 mr-2" />Save Draft
            </Button>
            <Button onClick={handleSendEmail} data-testid="button-send-email-submit" disabled={isSendingEmail}>
              {isSendingEmail ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              {isSendingEmail ? "Sending…" : "Send Email"}
            </Button>
            {onOpenAIDraft && (
              <Button variant="outline" onClick={onOpenAIDraft} data-testid="button-ai-assist-email">
                <Sparkles className="h-4 w-4 mr-2" />AI Assist
              </Button>
            )}
            <Button variant="outline" onClick={handleClose} data-testid="button-cancel-email">Cancel</Button>
          </div>
        </div>
      </div>
    </>
  );

  if (isPopupMode) return panelContent;
  return createPortal(panelContent, document.body);
}
